import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useTheme } from '@emotion/react';
import { useMediaQuery } from '@react-hookz/web';
import { useBreakpointUp } from '@travel-frontend/core';
import { useTranslation } from 'next-i18next';
import { useEffect } from 'react';
import { ImageCarousel, ImageSlider, useSwipeableImages } from '../../../../components/Image/index.js';
import { CategoryTabs } from '../../../../components/ImageGalleryLayer/components/CategoryTabs/index.js';
import { Button } from '../../../../components/ImageGalleryLayer/components/MainContent/components/Button.js';
import { ButtonContainer } from '../../../../components/ImageGalleryLayer/components/MainContent/components/ButtonContainer.js';
import { CarouselContainer } from '../../../../components/ImageGalleryLayer/components/MainContent/components/CarouselContainer.js';
import { Container } from '../../../../components/ImageGalleryLayer/components/MainContent/components/Container.js';
import { Content } from '../../../../components/ImageGalleryLayer/components/MainContent/components/Content.js';
import { SliderContainer } from '../../../../components/ImageGalleryLayer/components/MainContent/components/SliderContainer.js';
import { useGroupImages } from '../../../../components/ImageGalleryLayer/hooks/useGroupImages.js';
import { useTracking } from '../../../../hooks/index.js';
import { priceFormatter } from '../../../../utils/index.js';
export const MainContent = ({
  images,
  activeTabIndex,
  onActiveTabIndexChange,
  activeImageIndex,
  onActiveImageIndexChange,
  ctaButton: ctaButtonProperty,
  offerPrice,
  onOfferButtonClick
}) => {
  const {
    t
  } = useTranslation('imageGalleryLayer');
  const mdUp = useBreakpointUp('md');
  const lgUp = useBreakpointUp('lg');
  const {
    trackClickEvent
  } = useTracking();
  const isMobile = useMediaQuery('(max-device-width: 932px)');
  const isLandscape = useMediaQuery('(orientation: landscape)');
  const renderSlider = !(isMobile && isLandscape);
  const theme = useTheme();
  const {
    groupedData,
    activeImages,
    smallImages
  } = useGroupImages(images, activeTabIndex);
  const {
    goBack: handleCarouselGoBack,
    getInternalProps: carouselGetInternalProps,
    goForward: handleCarouselGoForward,
    goToImage: handleCarouselGoToImage,
    activeSlideIndex: carouselActiveSlideIndex
  } = useSwipeableImages({
    imageCount: activeImages.length,
    imagesPerSlide: 1
  });
  const {
    goForward: handleSliderGoForward,
    goBack: handleSliderGoBack,
    getInternalProps: sliderGetInternalProps,
    goToImage: sliderGoToImage
  } = useSwipeableImages({
    imageCount: activeImages.length,
    imagesPerSlide: lgUp ? 6 : mdUp ? 4 : 3
  });
  const ctaButtonElement = offerPrice && _jsx(Button, {
    onClick: onOfferButtonClick,
    children: t('offersFrom', {
      value: priceFormatter(offerPrice.locale, offerPrice.currency).format(offerPrice.amount)
    })
  });
  useEffect(() => {
    if (activeImageIndex !== undefined) {
      handleCarouselGoToImage(activeImageIndex);
    }
  }, [handleCarouselGoToImage, activeImageIndex]);
  useEffect(() => {
    if (carouselActiveSlideIndex >= 0) {
      sliderGoToImage(carouselActiveSlideIndex);
    }
  }, [carouselActiveSlideIndex, sliderGoToImage]);
  return _jsxs(Container, {
    isLandscape: true,
    spacing: isLandscape ? 0 : 1.25,
    children: [_jsx(Content, {
      justifyContent: "center",
      children: _jsx(CarouselContainer, {
        children: _jsx(ImageCarousel, {
          ...carouselGetInternalProps(),
          slideIndex: carouselActiveSlideIndex,
          images: activeImages.map(activeImage => ({
            url: activeImage.image_url,
            provider: activeImage.provider ?? ''
          })),
          indentImage: lgUp,
          isFinite: true,
          hasCounter: true,
          onClickPrev: () => {
            handleCarouselGoBack();
            trackClickEvent('gallery_slider_previous', {
              event: 'gallery'
            });
          },
          onClickNext: () => {
            handleCarouselGoForward();
            trackClickEvent('gallery_slider_next', {
              event: 'gallery'
            });
          }
        })
      })
    }), renderSlider && _jsxs(SliderContainer, {
      children: [_jsx(CategoryTabs, {
        activeTabIndex: activeTabIndex,
        onTabChange: index => {
          onActiveImageIndexChange(0);
          sliderGoToImage(0);
          onActiveTabIndexChange(index);
        },
        backgroundColor: theme.colors.backgroundDark,
        color: theme.colors.contentInversePrimary,
        groupedData: groupedData,
        ctaButton: ctaButtonProperty,
        offerPrice: offerPrice,
        onOfferButtonClick: onOfferButtonClick
      }), _jsx(ImageSlider, {
        ...sliderGetInternalProps(),
        aspectRatio: [16, 9],
        images: smallImages.map(image => image.image_url),
        activeImage: carouselActiveSlideIndex,
        rounded: true,
        hoverable: true,
        spacing: mdUp ? 1.25 : 0.5,
        onClickPrev: () => {
          handleSliderGoBack();
          trackClickEvent('gallery_slider_previous', {
            event: 'gallery'
          });
        },
        onClickNext: () => {
          handleSliderGoForward();
          trackClickEvent('gallery_slider_next', {
            event: 'gallery'
          });
        },
        onClickImage: index => {
          onActiveImageIndexChange(index);
          handleCarouselGoToImage(index);
        }
      })]
    }), !lgUp && (ctaButtonProperty || offerPrice) && _jsx(ButtonContainer, {
      children: ctaButtonProperty || ctaButtonElement
    })]
  });
};